import React, { useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { MainAppReducer } from 'Types';
import * as appActions from 'Actions/app';
import { ContributionHeatmap } from './components/ContributionHeatmap';
import { HorizontalBarChart } from './components/HorizontalBarChart';
import { MinutesPlayedChart } from './components/MinutesPlayedChart';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { CustomCircularProgress } from 'Components/StyledComponents';
import './dashboard.css';
import { Box, Container } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { StatsCard } from './components/StatsCard';
import { LevelsCompletionChart } from './components/LevelsCompletionChart';
import { LevelProgressChart } from './components/LevelProgressChart';
import { RepertoireProgressChart } from './components/RepertoireProgressChart';
import { CommunityDashboardCard } from 'Containers/Community/CommunityDashboardCard';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';

const Dashboard = () => {
  const showCommunityHub = useFeatureFlag(FeatureFlag.USE_COMMUNITY_HUB);
  const auth = useAuthWrapperContext();
  const dispatch = useDispatch();
  const dashboardCacheInvalidated = useSelector(
    (state: MainAppReducer) => state.mainAppReducer.dashboardCacheInvalidated,
  );
  const { dashboardData } = useSelector((state: MainAppReducer) => state.mainAppReducer);

  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const authToken = await auth?.currentAuthToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/metrics/dashboard`,
        {
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${authToken}`,
          },
          timeout: 5000,
        },
      );
      if (response.status !== 200) {
        throw new Error('Network response was not ok');
      }
      dispatch(appActions.setDashboardData(response.data.data));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('There was a problem loading the dashboard. Please try reloading the page.');
    } finally {
      setLoading(false);
    }
  }, [auth, dispatch]);

  // Fetch dashboard data on mount and whenever the cache is invalidated (e.g. after
  // completing a repertoire piece or level). The reducer sets dashboardData to null
  // when dashboardCacheInvalidated is toggled, so this effect re-fetches fresh data.
  useEffect(() => {
    if (!dashboardData) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [dashboardCacheInvalidated, fetchData]);

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
      >
        <Menu selected={'Dashboard'} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#111827',
            color: 'white',
          }}
        >
          <div className="text-white text-xl">Loading dashboard...</div>
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
      >
        <Menu selected={'Dashboard'} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#111827',
            color: 'white',
            textAlign: 'center',
            padding: 3,
          }}
        >
          <div className="text-white text-xl mb-4">{error}</div>
          <button
            onClick={() => fetchData()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Try Again
          </button>
        </Box>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CustomCircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
      }}
    >
      <Menu selected={'Dashboard'} />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#111827',
          display: 'flex',
          paddingLeft: {
            xs: 'calc(var(--app-sidebar-width, 100px) + 16px)',
            sm: 'calc(var(--app-sidebar-width, 100px) + 16px)',
            md: 'calc(var(--app-sidebar-width, 100px) + 20px)',
            lg: 'calc(var(--app-sidebar-width, 100px) + 20px)',
            xl: 'calc(var(--app-sidebar-width, 100px) + 20px)',
          },
          paddingRight: { xs: 1, sm: 2, md: 3, lg: 5, xl: 6 },
          paddingTop: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
          paddingBottom: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        }}
      >
        <CssBaseline />
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '100%', md: '90rem', lg: '105rem', xl: '120rem' },
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3, lg: 4 } }}>
            {showCommunityHub && <CommunityDashboardCard />}
            {/* Top Section - 3x2 Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: { xs: 2, sm: 3, lg: 4 },
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <HorizontalBarChart data={dashboardData.recentLevelAverages} />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <MinutesPlayedChart
                  totalMinutes={dashboardData.totalMinutesPlayed}
                  weeklyMinutes={dashboardData.weeklyMinutes}
                />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <LevelsCompletionChart
                  levelsCompleted={dashboardData.levelsCompleted}
                  totalLevels={dashboardData.totalLevels}
                />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <RepertoireProgressChart data={dashboardData.runningRepertoireCompleted} />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <LevelProgressChart data={dashboardData.runningHighestLevel} />
              </Box>
              <Box sx={{ display: 'flex' }}>
                <StatsCard
                  totalActiveDays={dashboardData.totalActiveDays}
                  longestStreakDays={dashboardData.longestStreakDays}
                  currentStreakDays={dashboardData.currentStreakDays}
                />
              </Box>
            </Box>

            {/* Bottom Section - Play Sessions Heatmap */}
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <ContributionHeatmap
                weeklyIntensity={dashboardData.weeklyIntensity}
                totalSessionsLastYear={dashboardData.totalSessionsLastYear}
                longestStreakDays={dashboardData.longestStreakDays}
                currentStreakDays={dashboardData.currentStreakDays}
                totalActiveDays={dashboardData.totalActiveDays}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
